import {
  ParentProfileQuery,
  ParentProfileQueryVariables
} from "@outschool/gql-frontend-generated";
import { gql, useQuery } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { MeetingFragment } from "@outschool/ui-components-classroom";
import { LeaderFragment } from "@outschool/ui-components-website";
import { Loading } from "@outschool/ui-legacy-component-library";
import React from "react";
import { useLocation, useParams } from "react-router";

import { ActivityFragment } from "../../shared/client/fragments/ActivityFragments";
import SimpleLayout from "../components/layouts/SimpleLayout";
import { ParentProfilePage } from "../components/ParentProfile";
import { ParentFragment } from "../queries/ParentFragments";
import { SectionFragment } from "../queries/SectionFragments";
import NotFoundPage from "./NotFoundPage";

const PARENT_PROFILE_QUERY = gql`
  query ParentProfile($uid: ID) {
    publicProfile: publicProfileNew(uid: $uid) {
      parent {
        ...ParentFragment
        slug_id
        reviewCount
        uniqueActivityCount
        sectionsWithCurrentUser {
          filledSpaceCount
          ...SectionFragment
          meetings {
            ...MeetingFragment
          }
          activity {
            ...ActivityFragment
          }
          leader {
            ...LeaderFragment
          }
        }
      }
    }
  }
  ${ParentFragment}
  ${ActivityFragment}
  ${SectionFragment}
  ${MeetingFragment}
  ${LeaderFragment}
`;

export default function ParentProfile() {
  const { currentUser } = useSession();
  const location = useLocation();

  const params = useParams<{ uid?: string; profileLinkUid?: string }>();
  const profileUid = params.uid ?? (currentUser?.uid || "");

  const { error, loading, data } = useQuery<
    ParentProfileQuery,
    ParentProfileQueryVariables
  >(PARENT_PROFILE_QUERY, {
    variables: { uid: profileUid }
  });

  if (loading || error) {
    return (
      <SimpleLayout error={error} hideSignUpModal>
        <Loading />
      </SimpleLayout>
    );
  }

  if (!data?.publicProfile?.parent) {
    return <NotFoundPage error={error} />;
  }

  const parent = data.publicProfile.parent;
  const isCurrentUsersProfile = !currentUser
    ? false
    : !!(currentUser.uid === parent.uid && currentUser.uid && parent.uid);

  if (location.query.preview || !isCurrentUsersProfile) {
    return (
      <ParentProfilePage
        parent={parent}
        isCurrentUsersProfile={isCurrentUsersProfile}
        profileLinkUid={params.profileLinkUid}
        publicView={true}
      />
    );
  }

  return (
    <ParentProfilePage
      parent={parent}
      isCurrentUsersProfile={isCurrentUsersProfile}
      publicView={false}
    />
  );
}
